import "../styles/eventThemes.css";
import type { BalanceData, EmberSource, LiveEventData } from "@highmarch/domain";
import { perOf } from "@highmarch/domain";
import { IconName, icon } from "../iconsKit";
import { isPaintedIcon, paintedIconHtml, paintedIconUrl } from "../featureIcons";
import { portraitUrl } from "../portraits";
import { emberGlyph } from "./eventRewards";

/**
 * How an event looks and reads (MP-21): its theme class (the banner and the hub chip's tint, ui/styles/eventThemes.css),
 * its picture (a painted icon, a portrait, or Hunters' Week's featured enemy) and its "how to earn embers" lines, all
 * generated from the event's data (events.json `theme`, `art`, `sources`). Presentation only: what a signal pays comes
 * from the domain (meta/embers.ts).
 */

/** The class that tints an event's banner and chip: `evt-<theme>`. */
export const themeClass = (e: Pick<LiveEventData, "theme">): string => `evt-${e.theme.replace(/[^a-z]/g, "") || "ember"}`;

/** The featured enemy's display name, plural ("Brutes"), or "" when the week has none. */
export function quarryName(B: BalanceData, e: LiveEventData): string {
  const id = e.featured?.enemy;
  const name = id ? B.enemies.find((x) => x.id === id)?.name ?? id : "";
  return name ? `${name}s` : "";
}

/** A picture this size or larger carries the week's quarry as an inset (Hunters' Week); the hub chip's is too small. */
export const QUARRY_INSET_MIN_PX = 40;
/** The quarry inset's side, as a fraction of the picture's. */
const QUARRY_INSET = 0.5;

/**
 * The event's picture at `px`: `icon:<painted>` (each event's own, tools/art/icons3d/events.py), `portrait:<id>[:<tier>]`
 * or `featured` (the quarry); the ember otherwise. A week with a featured enemy (Hunters' Week) shows its quarry's
 * portrait as an inset on the picture's lower corner, from QUARRY_INSET_MIN_PX up.
 */
export function eventArtHtml(e: LiveEventData, px: number): string {
  const [kind, id = "", tier = "1"] = e.art.split(":");
  const quarry = e.featured?.enemy ?? "";
  if (kind === "icon" && isPaintedIcon(id)) {
    const pic = paintedIconHtml(id, px, "evpic");
    const inset = quarry && px >= QUARRY_INSET_MIN_PX ? portraitUrl(quarry) : null;
    if (!inset) return pic;
    const s = Math.round(px * QUARRY_INSET);
    return `<span class="evpicw">${pic}<img class="evinset" decoding="sync" src="${inset}" width="${s}" height="${s}" alt=""></span>`;
  }
  const pid = kind === "featured" ? quarry : kind === "portrait" ? id : "";
  const url = pid ? portraitUrl(pid, Number(tier) || 1) : null;
  return url ? `<img class="evpic portrait" decoding="sync" src="${url}" width="${px}" height="${px}" alt="">` : emberGlyph(px);
}

/** The event's picture as one image URL (the away card's line): its painted icon or portrait; null for the ember glyph. */
export function eventArtUrl(e: LiveEventData): string | null {
  const [kind, id = "", tier = "1"] = e.art.split(":");
  if (kind === "icon" && isPaintedIcon(id)) return paintedIconUrl(id);
  const pid = kind === "featured" ? e.featured?.enemy ?? "" : kind === "portrait" ? id : "";
  return pid ? portraitUrl(pid, Number(tier) || 1) : null;
}

/** One way to earn, as the Events sheet lists it. */
export interface EarnLine { readonly icon: IconName; readonly label: string; readonly amount: string; readonly note: string }

interface SignalText { readonly icon: IconName; readonly label: (per: number, quarry: string) => string; readonly verb?: (quarry: string) => string; readonly bonus?: boolean; readonly perRun?: string }

/** Each signal's words: the sheet's label (with `per`), the imperative for the hub's line, whether it stacks on a win. */
const SIGNALS: Readonly<Record<string, SignalText>> = {
  win: { icon: "sword", label: () => "Win a battle", verb: () => "win battles", perRun: "" },
  loss: { icon: "flag", label: () => "Lose a battle" },
  lossStage: { icon: "flag", label: (per) => `A loss: every ${per} waves cleared`, perRun: "a battle" },
  modifiedWin: { icon: "flame", label: () => "Heroic or Trial win", bonus: true },
  heroicWin: { icon: "flame", label: () => "Heroic win", verb: () => "win on Heroic", bonus: true },
  trialWin: { icon: "bolt", label: () => "Weekly Trial win", verb: () => "win the Weekly Trial", bonus: true },
  bossWin: { icon: "skull", label: () => "Beat a boss level", verb: () => "beat boss levels", bonus: true },
  star: { icon: "star", label: () => "Each star earned", verb: () => "earn stars" },
  earlyCall: { icon: "fast", label: () => "Each wave called early", verb: () => "call waves early", perRun: "a battle" },
  noLeakWin: { icon: "shield", label: () => "Win with no leaks", verb: () => "win with no leaks", bonus: true },
  kill: { icon: "target", label: (per) => `Every ${per} raiders defeated`, verb: () => "defeat raiders", perRun: "a battle" },
  featuredKill: { icon: "target", label: (per, q) => `Every ${per === 1 ? "" : `${per} `}${q || "quarry"} defeated`, verb: (q) => `hunt ${q || "the quarry"}`, perRun: "a battle" },
  eliteKill: { icon: "skull", label: (per) => `Every ${per} elites defeated`, verb: () => "defeat elites", perRun: "a battle" },
  rareKill: { icon: "trophy", label: () => "Defeat a rare raider", verb: () => "slay rares" },
  order: { icon: "bag", label: () => "Daily Order claimed", verb: () => "finish Orders" },
  upgrade: { icon: "builder", label: () => "Upgrade completed", verb: () => "complete upgrades" },
  tribute: { icon: "coin", label: (per) => `Tribute: every ${per} h collected`, verb: () => "collect Tribute" },
  dailyRoad: { icon: "map", label: () => "Daily Road win", verb: () => "win the Daily Road" },
};
const textOf = (s: EmberSource): SignalText => SIGNALS[s.signal] ?? { icon: "star", label: () => s.signal };

/** The event's sources as the Events sheet lists them, in data order: icon, words, "+12" (a bonus reads "+6 more"), the caps. */
export function earnLines(B: BalanceData, e: LiveEventData): EarnLine[] {
  const quarry = quarryName(B, e), span = e.recurring ? "this week" : "this event";
  return e.sources.map((s) => {
    const t = textOf(s), per = perOf(e, s);
    const notes: string[] = [];
    if (s.runCap > 0) notes.push(`up to ${s.runCap} ${t.perRun ? t.perRun : "a time"}`);
    if (s.cap > 0) notes.push(`up to ${s.cap} ${span}`);
    return { icon: t.icon, label: t.label(per, quarry), amount: `+${s.amount}${t.bonus ? " more" : ""}`, note: notes.join(" · ") };
  });
}

/** MP-21: an event mode's week names its mode, not its ember sources (the mode's prizes are its main embers). */
const MODE_HOW: Readonly<Record<string, string>> = { boss: "Beat the week's boss, tier by tier", race: "Race the week's road for medals", odyssey: "Cross three maps on one fortress" };

/** The week's way in a few words for the hub ("Beat boss levels and win on Heroic"): the first two sources that have a verb and are not a win. */
export function earnHow(B: BalanceData, e: LiveEventData): string {
  if (MODE_HOW[e.kind]) return MODE_HOW[e.kind];
  const quarry = quarryName(B, e);
  const verbs = e.sources.map((s) => textOf(s).verb?.(quarry) ?? "").filter((v) => v !== "");
  const lead = verbs.filter((v) => v !== "win battles");
  const pick = (lead.length >= 2 ? lead : verbs).slice(0, 2);
  const line = pick.length === 2 ? `${pick[0]} and ${pick[1]}` : pick[0] ?? "win battles";
  return line.charAt(0).toUpperCase() + line.slice(1);
}

/** The Events sheet's ways to earn: one compact chip per source (its glyph, a few words, the embers), two to a row. */
export function earnHtml(B: BalanceData, e: LiveEventData): string {
  const rows = earnLines(B, e).map((l) => `<li>${icon(l.icon, 15)}<span class="lb">${l.label}</span><b class="am">+${l.amount.replace(/^\+/, "").replace(" more", "")}</b></li>`).join("");
  return `<section class="evearn"><h4 class="disp">${emberGlyph(18)} EARN ${e.currency.toUpperCase()}</h4><ul>${rows}</ul></section>`;
}
